import { Typography } from '@/components/ui/Typography';
import {
  DATE_FORMATS,
  RECEIVING_CONFIRMED_DAYS_BEFORE,
} from '@/configs/settings';
import { useReceivingPage } from '@/hooks/useReceivingPage';
import {
  StyledActiveTabFilters,
  StyledAutocompleteWrapper,
  StyledCalendarWrapper,
  StyledCheckbox,
  StyledCheckboxWrapper,
  StyledDateFiltersWrapper,
  StyledDatePicker,
  StyledDateWrapper,
  StyledDeliveryNumberFilterWrapper,
  StyledFilters,
} from '@/pages/Receiving/PageReceiving/style';
import {
  convertDateToDateTime,
  curDate,
  daysBefore,
  isSameDay,
} from '@/utils/date';
import DateFnsUtils from '@date-io/date-fns';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  receivingPage: ReturnType<typeof useReceivingPage>;
  setSearchDeliveries?: React.Dispatch<React.SetStateAction<boolean>>;
  last?: {
    startDate: Date | null;
    endDate: Date | null;
    deliveryNumber: string;
    searchType: 'DATES' | 'DELIVERY' | null;
  };
};

export const ReceivingConfirmedFilters = ({
  receivingPage,
  setSearchDeliveries,
  last
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { DATEPICKER } = DATE_FORMATS;

  const today = curDate(DATEPICKER);

  const {
    state: { startDate, endDate, deliveryNumber, mode },
    actions: {
      activateDateMode,
      activateDeliveryMode,
      clearMode,
      resetDelivery,
      resetFilters,
      setDeliverySearch,
      setStartDateOnly,
      setEndDateOnly,
    },
  } = receivingPage;

  const minDate = daysBefore(RECEIVING_CONFIRMED_DAYS_BEFORE, DATEPICKER);

  const handleOpenPicker = useCallback(() => {
    activateDateMode();
    resetDelivery();
  }, [activateDateMode, resetDelivery]);

  const handleClosePicker = useCallback(() => {
    clearMode();
  }, [clearMode]);

  const handleDeliveryFocus = useCallback(() => {
    activateDeliveryMode();
    setStartDateOnly(null);
    setEndDateOnly(null);
  }, [activateDeliveryMode, setEndDateOnly, setStartDateOnly]);

  const handleDeliveryBlur = useCallback(() => {
    clearMode();

    if (deliveryNumber.trim()) return;

    if (last?.searchType === 'DATES') {
      const currentMatchesLast =
        isSameDay(startDate, last.startDate) &&
        isSameDay(endDate, last.endDate) &&
        deliveryNumber.trim() === last.deliveryNumber.trim();

      if (!currentMatchesLast) {
        receivingPage.actions.setStartDateOnly(last.startDate);
        receivingPage.actions.setEndDateOnly(last.endDate);
        // niente setSearchDeliveries(true)
      }
      return;
    }

    if (last?.searchType === 'DELIVERY') {
      receivingPage.actions.resetDates();
      setSearchDeliveries?.(true);
    }
  }, [
    clearMode,
    deliveryNumber,
    endDate,
    receivingPage.actions,
    setSearchDeliveries,
    startDate,
  ]);

  return (
    <StyledFilters>
      <StyledDateFiltersWrapper>
        <Typography size="sm">
          <Typography size="sm" font="heavy">
            {t('receiving.searchByDateTitle')}{' '}
          </Typography>
          <Typography size="sm" font="light">
            {t('receiving.searchByDateMessage')}
          </Typography>
        </Typography>
        <StyledDateWrapper>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <StyledCalendarWrapper mt={2} $disabled={mode === 'DELIVERY'}>
              <StyledDatePicker
                autoOk
                disableToolbar
                placeholder={t('start.date')}
                inputVariant="outlined"
                id="start-date"
                variant="inline"
                InputLabelProps={{ shrink: true }}
                format={DATEPICKER}
                inputProps={{
                  defaultValue: undefined,
                }}
                value={
                  deliveryNumber
                    ? null
                    : startDate
                    ? convertDateToDateTime(startDate).toFormat(DATEPICKER)
                    : null
                }
                minDate={minDate}
                maxDate={
                  endDate
                    ? convertDateToDateTime(endDate).toFormat(DATEPICKER)
                    : today
                }
                onChange={(date): void => {
                  const newStartDate = new Date(date!.getTime());
                  setStartDateOnly(newStartDate);
                }}
                label={t('start.date')}
                disabled={mode === 'DELIVERY' || !!deliveryNumber}
                onOpen={handleOpenPicker}
                onClose={handleClosePicker}
              />
              <div className="helper">{'\u00A0'}</div>
            </StyledCalendarWrapper>
            <StyledCalendarWrapper mt={2} $disabled={mode === 'DELIVERY'}>
              <StyledDatePicker
                autoOk
                disableToolbar
                placeholder={t('end.date')}
                inputVariant="outlined"
                id="end-date"
                variant="inline"
                InputLabelProps={{ shrink: true }}
                disabled={mode === 'DELIVERY' || !startDate || !!deliveryNumber}
                format={DATEPICKER}
                value={
                  deliveryNumber
                    ? null
                    : endDate
                    ? convertDateToDateTime(endDate).toFormat(DATEPICKER)
                    : null
                }
                minDate={convertDateToDateTime(startDate! as Date).toFormat(
                  DATEPICKER
                )}
                maxDate={today}
                onChange={(date): void => {
                  const newEndDate = new Date(date!.getTime());
                  setEndDateOnly(newEndDate);
                }}
                label={t('end.date')}
                onOpen={handleOpenPicker}
                onClose={handleClosePicker}
              />
              <div className="helper">{'\u00A0'}</div>
            </StyledCalendarWrapper>
          </MuiPickersUtilsProvider>
        </StyledDateWrapper>
      </StyledDateFiltersWrapper>
      <StyledDeliveryNumberFilterWrapper>
        <Typography size="sm" font="heavy">
          <b>{t('receiving.searchByDeliveryNumberTitle')}</b>
        </Typography>
        <StyledAutocompleteWrapper>
          <TextField
            variant="outlined"
            onChange={(e): void => {
              const value = e.target.value;
              setDeliverySearch(value);
            }}
            value={deliveryNumber}
            onFocus={handleDeliveryFocus}
            onBlur={handleDeliveryBlur}
            label={t('receiving.searchByDeliveryNumberMessage')}
            disabled={mode === 'DATES'}
            helperText={t('receiving.searchByDeliveryNumberHelper')}
            inputProps={{ maxLenght: 30, minLenght: 3 }}
            InputProps={{
              endAdornment: deliveryNumber ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      resetFilters();
                      setSearchDeliveries?.(true);
                    }}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </StyledAutocompleteWrapper>
      </StyledDeliveryNumberFilterWrapper>
    </StyledFilters>
  );
};

export const ReceivingActiveFilters = ({
  receivingPage,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const {
    state: { filters },
    actions: { toggleFilter },
  } = receivingPage;

  return (
    <StyledActiveTabFilters>
      <Typography size={'sm'} font={'light'}>
        {t('receiving.filterByStatus')}
      </Typography>
      <StyledCheckboxWrapper>
        <StyledCheckbox
          checked={filters.new}
          onChange={(): void => toggleFilter('new')}
        />
        <Typography size={'sm'} font={'medium'}>
          {t('receiving.status.new')}
        </Typography>
      </StyledCheckboxWrapper>{' '}
      <StyledCheckboxWrapper>
        <StyledCheckbox
          checked={filters.partial}
          onChange={(): void => toggleFilter('partial')}
        />
        <Typography size={'sm'} font={'medium'}>
          {t('receiving.status.partial')}
        </Typography>
      </StyledCheckboxWrapper>{' '}
      <StyledCheckboxWrapper>
        <StyledCheckbox
          checked={filters.inUse}
          onChange={(): void => toggleFilter('inUse')}
        />
        <Typography size={'sm'} font={'medium'}>
          {t('receiving.status.inUse')}
        </Typography>
      </StyledCheckboxWrapper>
    </StyledActiveTabFilters>
  );
};
